/**
 * Lightweight, dependency-free feedback sounds built on the Web Audio API:
 * the counter's cash-register chime, the appointment-reminder chime and a
 * short haptic buzz (Android/PWA). Every call is wrapped so a missing API or a
 * silent device can never throw or break the flow that triggered it.
 */

let audioContext = null;

const ensureContext = () => {
  if (!audioContext) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioContext = new Ctx();
  }
  if (audioContext.state === "suspended") {
    audioContext.resume().catch(() => {});
  }
  return audioContext;
};

/**
 * One oscillator note.
 *
 * `glideTo` sweeps the pitch across the note (the register "ding"); `attack`
 * fades the volume in over `attack` seconds so softer, longer tones — like the
 * reminder chime — start without a click.
 */
const tone = (
  ctx,
  { frequency, at, duration, volume, type = "sine", glideTo, attack = 0 },
) => {
  try {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, at);
    if (glideTo) {
      oscillator.frequency.exponentialRampToValueAtTime(glideTo, at + duration);
    }
    if (attack > 0) {
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.linearRampToValueAtTime(volume, at + attack);
    } else {
      gain.gain.setValueAtTime(volume, at);
    }
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(at);
    oscillator.stop(at + duration + 0.02);
  } catch {
    /* no-op */
  }
};

/** Short "ding" — two quick notes, the classic register sound. */
export function playChime() {
  try {
    const ctx = ensureContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    tone(ctx, { frequency: 880, at: now, duration: 0.15, volume: 0.07, glideTo: 880 * 1.4 });
    tone(ctx, { frequency: 1318.5, at: now + 0.12, duration: 0.25, volume: 0.07, glideTo: 1318.5 * 1.4 });
  } catch {
    /* no-op */
  }
}

/**
 * Appointment-reminder chime — a gentle three-note rise (E4 → E5 → B5) with a
 * soft attack. Warmer and longer-tailed than the register ding so a reminder
 * is distinguishable from a sale at a noisy counter, but quiet enough to loop
 * through a burst of due reminders without becoming a nuisance.
 */
export function playReminderChime() {
  try {
    const ctx = ensureContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    tone(ctx, { frequency: 329.63, at: now, duration: 0.5, volume: 0.035, attack: 0.04 });
    tone(ctx, { frequency: 659.25, at: now + 0.16, duration: 0.5, volume: 0.05, attack: 0.03 });
    tone(ctx, { frequency: 987.77, at: now + 0.32, duration: 0.7, volume: 0.045, attack: 0.03 });
  } catch {
    /* no-op */
  }
}

/** Haptic tick — patterns under ~100ms feel like a confirmation, not a ring. */
export function buzz(pattern = 30) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* no-op */
  }
}

/**
 * Creates (and resumes) the shared AudioContext. Browsers gate Web Audio
 * behind a user gesture, so the reminder poller calls this from the first
 * pointer/key event: by the time a timer-driven reminder chime fires, the
 * context is already running and the sound is not silently dropped.
 */
export function primeAudio() {
  try {
    ensureContext();
  } catch {
    /* no-op */
  }
}

/** One call for "sale completed": haptic + chime together. */
export function successFeedback() {
  buzz(35);
  playChime();
}

export default { playChime, playReminderChime, buzz, successFeedback, primeAudio };
